import { patchState, signalStore, withMethods, withState } from "@ngrx/signals";
import { initialState } from "@services/global-state/global-state.model";
import { BannerVariant } from "@vg-constellation/angular-17/banner";
import { EstateProfile, Person, Trust, sanitizeEstateProfileData } from "@models/estateProfile.model";
import { TableType } from "../../types/TableType";
import { WebLogger } from "@weps/shared-http";

export const GlobalStateStore = signalStore(
  { providedIn: "root" },
  withState(initialState),
  withMethods((store) => ({
    updateModalSyncState(newState: "error" | "success" | "loading" | "not-started"): void {
      patchState(store, { modalSyncState: newState });
    },
    updateModalSyncMessage(newState: string): void {
      patchState(store, { modalSyncMessage: newState });
    },
    updateModalSyncInProgress(inProgress: boolean): void {
      patchState(store, { modalSyncInProgress: inProgress });
    },
    updateBannerState(showBanner: boolean, bannerVariant: BannerVariant, bannerContent: string): void {
      patchState(store, { bannerState: { showBanner, bannerVariant, bannerContent } });
    },
    updateEstateProfile(estateProfile: EstateProfile): void {
      const sanitizedProfile = sanitizeEstateProfileData(estateProfile);
      patchState(store, { estateProfile: sanitizedProfile });

      // Estate profile ID, name, and spouse data are now accessed directly from estateProfile object
    },
    updateEstateProfileId(estateProfileId: string): void {
      const currentProfile = store.estateProfile?.();

      if (currentProfile) {
        // Create a new object with the updated estateProfileId
        patchState(store, {
          estateProfile: {
            ...currentProfile,
            estateProfileId
          }
        });
      } else {
        WebLogger.log({ message: "GlobalState cannot update estateProfileId - no estate profile exists", level: "warn" });
      }
    },
    findSpouseData(): Person | undefined {
      const estateProfile = store.estateProfile?.();
      if (!estateProfile?.people || !Array.isArray(estateProfile.people)) {
        return undefined;
      }

      return estateProfile.people.find(person => person?.isSpouse);
    },

    // Guided Flow State Management Methods
    setGuidedFlowInitialState(routeKey: string, stateObj: Record<string, any>): void {
      const currentState = store.guidedFlowState();
      patchState(store, {
        guidedFlowState: {
          ...currentState,
          [routeKey]: {
            ...currentState[routeKey],
            ...stateObj
          }
        }
      });
    },

    updateGuidedFlowTempState(routeKey: string, stateObj: Record<string, any>): void {
      const currentTempState = store.guidedFlowTempState();
      const currentState = store.guidedFlowState();

      // Initialize temp state for this route if it doesn't exist
      if (!currentTempState[routeKey]) {
        patchState(store, {
          guidedFlowTempState: {
            ...currentTempState,
            [routeKey]: { ...currentState[routeKey] || {} }
          }
        });
      }

      const updatedTempState = store.guidedFlowTempState();
      patchState(store, {
        guidedFlowTempState: {
          ...updatedTempState,
          [routeKey]: {
            ...updatedTempState[routeKey],
            ...stateObj
          }
        }
      });
    },

    mergeGuidedFlowTempState(routeKey: string): void {
      const tempState = store.guidedFlowTempState();
      if (tempState[routeKey]) {
        const currentState = store.guidedFlowState();
        patchState(store, {
          guidedFlowState: {
            ...currentState,
            [routeKey]: {
              ...currentState[routeKey],
              ...tempState[routeKey]
            }
          },
          guidedFlowTempState: {}
        });
      }
    },

    resetGuidedFlowTempState(): void {
      patchState(store, { guidedFlowTempState: {} });
    },

    resetGuidedFlowState(): void {
      patchState(store, { guidedFlowState: {}, guidedFlowTempState: {} });
    },

    getGuidedFlowState(routeKey: string): Record<string, any> {
      return store.guidedFlowState()[routeKey] || {};
    },

    getGuidedFlowTempState(routeKey: string): Record<string, any> {
      return store.guidedFlowTempState()[routeKey] || {};
    },

    // Current Step Management Methods
    getCurrentStep(routeKey: string): number {
      const state = store.guidedFlowState()[routeKey];
      return state?.currentStep || 1;
    },

    setCurrentStep(routeKey: string, step: number): void {
      const currentState = store.guidedFlowState();
      patchState(store, {
        guidedFlowState: {
          ...currentState,
          [routeKey]: {
            ...currentState[routeKey],
            currentStep: step
          }
        }
      });
    },

    // Trust Management Methods
    getTrusts(): Trust[] {
      const estateProfile = store.estateProfile?.();
      return estateProfile?.trusts || [];
    },

    createTrust(trustData: {
      legalName: string;
      displayName: string;
      trustType: string;
      settlors: { memberType: string; householdMemberId: string }[];
    }): Trust {
      const newTrust: Trust = {
        legalName: trustData.legalName,
        displayName: trustData.displayName,
        trustType: trustData.trustType,
        settlors: trustData.settlors
      };

      // Add to estate profile
      const estateProfile = store.estateProfile?.();
      if (estateProfile) {
        if (!estateProfile.trusts) {
          estateProfile.trusts = [];
        }
        estateProfile.trusts.push(newTrust);
        patchState(store, { estateProfile: { ...estateProfile } });
      }

      return newTrust;
    },

    //Used to identify if it's a Vanguard or Manually Entered Entity
    isVanguard(routeKey: string): boolean {
      const currentState = store.guidedFlowState()[routeKey];
      return currentState.tableType ? currentState.tableType === TableType.VANGUARD: false
    },
  })),
);
